import { useMemo } from 'react';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from 'react-router-dom';
import {
  ChevronRightLineIcon,
  ExternalLinkLineIcon
} from '@ifrc-go/icons'
import { type ButtonFeatureProps } from '@ifrc-go/ui';
import { useButtonFeatures } from '@ifrc-go/ui/hooks';
import {
  _cs,
  isDefined,
  isFalsyString,
  isNotDefined
} from '@togglecorp/fujs'

import styles from './styles.module.css';

type CommonLinkProps = {
  className?: string;
  actions?: React.ReactNode;
  actionsContainerClassName?: string;
  disabled?: boolean;
  icons?: React.ReactNode;
  iconsContainerClassName?: string;
  linkElementClassName?: string;
  variant?: ButtonFeatureProps['variant'];
  withLinkIcon?: boolean;
  withUnderline?: boolean;
  spacing?: ButtonFeatureProps['spacing'];
  children: React.ReactNode;
  onClick?: () => void;
}

type ExternalLinkProps = {
  external: true,
  href: string | undefined | null;
  to?: never;
}

interface InternalLinkProps extends RouterLinkProps {
  external?: never;
  href?: never;
}

export type Props = CommonLinkProps & (InternalLinkProps | ExternalLinkProps);

function Link(props: Props) {
  const {
    actions,
    actionsContainerClassName,
    children: childrenFromProps,
    className,
    disabled: disabledFromProps,
    icons,
    iconsContainerClassName,
    linkElementClassName,
    withUnderline,
    withLinkIcon,
    variant = 'tertiary',
    spacing,

    external,
    to,
    href,

    ...otherProps
  } = props;

  const nonLink = external ? isFalsyString(href) : isNotDefined(to);
  const disabled = disabledFromProps || nonLink;

  const {
    children: content,
    className: containerClassName,
  } = useButtonFeatures({
    className: styles.content,
    icons,
    children: childrenFromProps,
    variant,
    disabled: disabledFromProps,
    spacing,
    actions: (isDefined(actions) || withLinkIcon) ? (
      <>
        {actions}
        {withLinkIcon && external && (
          <ExternalLinkLineIcon />
        )}
        {withLinkIcon && !external && (
          <ChevronRightLineIcon className={styles.forwardIcon} />
        )}
      </>
    ) : null,
    iconsContainerClassName,
    actionsContainerClassName,
  });

  const children = useMemo(
    () => {
      if (nonLink) {
        return (
          <div className={containerClassName}>
            {content}
          </div>
        );
      }
      // eslint-disable-next-line react/destructuring-assignment
      if (external) {
        if (isNotDefined(href)) {
          return null;
        }

        return (
          <a
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...otherProps}
            className={_cs(
              linkElementClassName,
              styles.linkElement,
              containerClassName,
            )}
            target="_blank"
            rel="noopener noreferrer"
            href={href}
          >
            {content}
          </a>
        );
      }

      if (isNotDefined(to)) {
        return null;
      }

      return (
        <RouterLink
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...otherProps}
          className={_cs(
            linkElementClassName,
            styles.linkElement,
            containerClassName,
          )}
          to={to}
        >
          {content}
        </RouterLink>
      );
    },
    [
      linkElementClassName,
      containerClassName,
      content,
      otherProps,
      to,
      external,
      href,
      nonLink,
    ],
  );


  return (
    <div
      className={_cs(
        styles.link,
        nonLink && styles.nonLink,
        withUnderline && styles.underline,
        disabled && styles.disabled,
        variant === 'dropdown-item' && styles.dropdownItem,
        variant === 'tertiary' && styles.tertiary,
        className,
      )}
    >
      {children}
    </div>
  );
}


export default Link;
